import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, getDocs, query, orderBy, doc, updateDoc, serverTimestamp, writeBatch, arrayUnion } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { FaTimes, FaTasks, FaExclamationCircle, FaClock, FaCheck, FaCalendarAlt, FaChevronDown, FaChevronUp, FaBan } from 'react-icons/fa';
import styles from './MyTasksModal.module.css';

const PRIORITY_CONFIG = {
  high: { label: 'Yüksek', color: '#e53e3e' },
  medium: { label: 'Orta', color: '#dd6b20' },
  low: { label: 'Düşük', color: '#38a169' }
};

const STATUS_CONFIG = {
  pending: { label: 'Beklemede', color: '#718096', icon: <FaClock /> },
  'in-progress': { label: 'Devam Ediyor', color: '#3182ce', icon: <FaTasks /> },
  completed: { label: 'Tamamlandı', color: '#38a169', icon: <FaCheck /> },
  cancelled: { label: 'İptal Edildi', color: '#e53e3e', icon: <FaBan /> }
};

const MyTasksModal = ({ isOpen, onClose }) => {
  const { isDarkMode } = useTheme();
  const { employeeId } = useUserProfile();
  const modalRef = useRef(null);

  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedTask, setExpandedTask] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [unseenTaskIds, setUnseenTaskIds] = useState(() => new Set());

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const isOverdue = (task) => {
    if (!task.dueDate || task.status === 'completed' || task.status === 'cancelled') return false;
    const due = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
    return due < new Date();
  };

  const fetchTasks = useCallback(async () => {
    try {
      setIsLoading(true);
      const tasksRef = collection(db, 'Tasks');
      const q = query(tasksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const tasksList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setTasks(tasksList);

      // Mark unseen tasks assigned to me as viewed
      if (employeeId) {
        const unseenMine = tasksList.filter(task => {
          const isMine =
            task.assigneeId === employeeId ||
            (task.assignees && task.assignees.some(a => a.id === employeeId));
          if (!isMine) return false;
          return !(Array.isArray(task.viewedBy) && task.viewedBy.includes(employeeId));
        });
        setUnseenTaskIds(new Set(unseenMine.map(t => t.id)));
        if (unseenMine.length > 0) {
          const batch = writeBatch(db);
          unseenMine.forEach(t => {
            batch.update(doc(db, 'Tasks', t.id), { viewedBy: arrayUnion(employeeId) });
          });
          await batch.commit();
        }
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setIsLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (isOpen) {
      fetchTasks();
      document.body.style.overflow = 'hidden';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen, fetchTasks]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === modalRef.current) onClose();
  };

  const myTasks = useMemo(() => {
    // All users (including admins) only see their own tasks
    let filtered = tasks.filter(task =>
      task.assigneeId === employeeId ||
      (task.assignees && task.assignees.some(a => a.id === employeeId))
    );

    if (statusFilter === 'active') {
      filtered = filtered.filter(t => t.status === 'pending' || t.status === 'in-progress');
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    // Sort: overdue first, then by priority (high > medium > low), then by due date
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return filtered.sort((a, b) => {
      const aOverdue = isOverdue(a) ? 0 : 1;
      const bOverdue = isOverdue(b) ? 0 : 1;
      if (aOverdue !== bOverdue) return aOverdue - bOverdue;
      const aPri = priorityOrder[a.priority] ?? 3;
      const bPri = priorityOrder[b.priority] ?? 3;
      if (aPri !== bPri) return aPri - bPri;
      return 0;
    });
  }, [tasks, employeeId, statusFilter]);

  const stats = useMemo(() => {
    // All users (including admins) only see stats for their own tasks
    const mine = tasks.filter(t =>
      t.assigneeId === employeeId ||
      (t.assignees && t.assignees.some(a => a.id === employeeId))
    );
    return {
      pending: mine.filter(t => t.status === 'pending').length,
      inProgress: mine.filter(t => t.status === 'in-progress').length,
      completed: mine.filter(t => t.status === 'completed').length,
      overdue: mine.filter(t => isOverdue(t)).length
    };
  }, [tasks, employeeId]);

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const taskRef = doc(db, 'Tasks', taskId);
      const updateData = { status: newStatus, updatedAt: serverTimestamp() };
      if (newStatus === 'completed') updateData.completedAt = serverTimestamp();
      await updateDoc(taskRef, updateData);
      fetchTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`${styles.overlay} ${isDarkMode ? styles.dark : ''}`} ref={modalRef} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <FaTasks className={styles.headerIcon} />
            <h2 className={styles.modalTitle}>Görevlerim</h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Kapat">
            <FaTimes />
          </button>
        </div>

        {/* Mini Stats */}
        <div className={styles.statsRow}>
          <div className={styles.statChip}>
            <FaClock style={{ color: '#718096' }} />
            <span>{stats.pending} Bekleyen</span>
          </div>
          <div className={styles.statChip}>
            <FaTasks style={{ color: '#3182ce' }} />
            <span>{stats.inProgress} Devam Eden</span>
          </div>
          <div className={styles.statChip}>
            <FaCheck style={{ color: '#38a169' }} />
            <span>{stats.completed} Tamamlanan</span>
          </div>
          {stats.overdue > 0 && (
            <div className={`${styles.statChip} ${styles.overdueChip}`}>
              <FaExclamationCircle style={{ color: '#e53e3e' }} />
              <span>{stats.overdue} Gecikmiş</span>
            </div>
          )}
        </div>

        {/* Filter Tabs */}
        <div className={styles.filterTabs}>
          {[
            { key: 'active', label: 'Aktif' },
            { key: 'completed', label: 'Tamamlanan' },
            { key: 'all', label: 'Tümü' }
          ].map(tab => (
            <button
              key={tab.key}
              className={`${styles.filterTab} ${statusFilter === tab.key ? styles.filterTabActive : ''}`}
              onClick={() => setStatusFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>Görevler yükleniyor...</p>
            </div>
          ) : myTasks.length === 0 ? (
            <div className={styles.emptyState}>
              <FaCheck className={styles.emptyIcon} />
              <p>{statusFilter === 'active' ? 'Aktif göreviniz bulunmuyor' : 'Görev bulunamadı'}</p>
            </div>
          ) : (
            <div className={styles.taskList}>
              {myTasks.map(task => (
                <div
                  key={task.id}
                  className={`${styles.taskCard} ${isOverdue(task) ? styles.overdueCard : ''} ${expandedTask === task.id ? styles.taskCardExpanded : ''} ${unseenTaskIds.has(task.id) ? styles.unseenCard : ''}`}
                >
                  <div className={styles.taskRow} onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}>
                    <div className={styles.taskPriorityBar} style={{ backgroundColor: PRIORITY_CONFIG[task.priority]?.color || '#718096' }} />
                    <div className={styles.taskInfo}>
                      <div className={styles.taskTitleRow}>
                        <h4 className={styles.taskTitle}>{task.title}</h4>
                        {unseenTaskIds.has(task.id) && <span className={styles.newBadge}>YENİ</span>}
                        {isOverdue(task) && <span className={styles.overdueBadge}>Gecikmiş</span>}
                      </div>
                      <div className={styles.taskMeta}>
                        <span
                          className={styles.statusBadge}
                          style={{ color: STATUS_CONFIG[task.status]?.color, borderColor: STATUS_CONFIG[task.status]?.color }}
                        >
                          {STATUS_CONFIG[task.status]?.label}
                        </span>
                        <span className={styles.priorityLabel} style={{ color: PRIORITY_CONFIG[task.priority]?.color }}>
                          {PRIORITY_CONFIG[task.priority]?.label}
                        </span>
                        {task.dueDate && (
                          <span className={`${styles.dueDate} ${isOverdue(task) ? styles.overdueDateText : ''}`}>
                            <FaCalendarAlt /> {formatDate(task.dueDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className={styles.expandIcon}>
                      {expandedTask === task.id ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>

                  {expandedTask === task.id && (
                    <div className={styles.taskExpanded}>
                      {task.description && (
                        <p className={styles.taskDescription}>{task.description}</p>
                      )}
                      {task.assignees && task.assignees.length > 0 && (
                        <div className={styles.assigneesRow}>
                          <span className={styles.assigneesLabel}>Atanan:</span>
                          <span>{task.assignees.map(a => a.name).join(', ')}</span>
                        </div>
                      )}
                      <div className={styles.statusActions}>
                        <span className={styles.changeLabel}>Durumu Değiştir:</span>
                        <div className={styles.statusButtons}>
                          {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                            <button
                              key={key}
                              className={`${styles.statusBtn} ${task.status === key ? styles.statusBtnActive : ''}`}
                              style={{
                                borderColor: val.color,
                                color: task.status === key ? '#fff' : val.color,
                                backgroundColor: task.status === key ? val.color : 'transparent'
                              }}
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(task.id, key); }}
                            >
                              {val.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyTasksModal;
